"use client";
import { kv } from "@vercel/kv";
import axios from "axios";
import {
  getAuthToken,
  getFollowerAuthToken,
  removeAuthToken,
} from "utils/authentication";
import { getTimezone } from "utils/common";
import config from "utils/config";
import { getCookie } from "utils/cookie-manager";
import { mError } from "utils/logger";

const controller = new AbortController();

async function fetchCachedData(key: string) {
  try {
    return await kv.get(key);
  } catch (e) {
    console.error(`Failed to fetch cache for ${key}`, e);
    return null;
  }
}

async function updateCacheData(key: string, value: any) {
  await kv.set(key, value);
}

const axiosInstance = axios.create({
  baseURL: config.API_URL,
  headers: {
    "Accept-Language": "en",
  },
  signal: controller.signal,
});

axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error?.response?.status === 401) {
      removeAuthToken();
      mError("Unauthorized");
      window.open("/sign-in", "_self");
    }
    return Promise.reject(error);
  }
);

axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error?.response?.status === 403) {
      removeAuthToken();
      mError("Access Denied");
      window.open("/sign-in", "_self");
    }
    return Promise.reject(error);
  }
);

const getConfig = (params, extraConfigs, isUpdateCall) => {
  // console.log(`Extra configs are ${JSON.stringify(extraConfigs)}`);

  let config: any = {
    ...(isUpdateCall ? { body: params } : { params: params }),
  };

  const token = getAuthToken();
  const followerToken = getFollowerAuthToken();
  if (extraConfigs && extraConfigs?.viaOauth) {
    config.headers = {
      ...config?.headers,
      Authorization: `Token ${extraConfigs?.token || token}`,
    };
  }

  if (extraConfigs && extraConfigs?.country) {
    config.headers = {
      ...config?.headers,
      "X-CLIENT-COUNTRY": extraConfigs?.country,
    };
  } else {
    config.headers = {
      ...config?.headers,
      "X-CLIENT-COUNTRY": getBrowserCountry() || "IN",
    };
  }
  if (extraConfigs && extraConfigs?.captcha) {
    config.headers = {
      ...config?.headers,
      "X-Captcha-Token": extraConfigs?.captcha,
    };
  }
  if (extraConfigs && extraConfigs?.glocalOcID) {
    config.headers = {
      ...config?.headers,
      "X-OC-REQUEST-ID": extraConfigs?.glocalOcID,
    };
  }
  if (extraConfigs && extraConfigs?.followerToken) {
    config.headers = {
      ...config?.headers,
      "X-follower-token": followerToken,
    };
  }
  if (extraConfigs && extraConfigs?.searchTrackingID) {
    config.headers = {
      ...config?.headers,
      "X-tracking-id": extraConfigs?.searchTrackingID,
    };
  }
  if (extraConfigs && extraConfigs?.utmData) {
    config.headers = {
      ...config?.headers,
      "X-utm-params": JSON.stringify(extraConfigs?.utmData),
    };
  }
  if (extraConfigs && extraConfigs?.embedIdentifier) {
    config.headers = {
      ...config?.headers,
      "X-Web-UI": "embed",
    };
  }
  if (extraConfigs && extraConfigs.browserName) {
    config.headers = {
      ...config.headers,
      "X-Browser-Type": String(extraConfigs?.browserName).toLowerCase(),
    };
  }
  config = {
    ...config,
    headers: {
      ...config?.headers,
      "X-timezone": getTimezone(),
      "X-user-agent": "topmate",
      "X-DEVICE-ID": getCookie("topmate_device_id"),
    },
  };
  return config;
};

const axiosRequest = async (apiKey, method, dataObj, extraConfigs) => {
  const methods = method.toLowerCase();
  if (methods === "get" || methods === "delete" || methods === "head") {
    let params = {};
    params = dataObj ? dataObj : params;
    const config = getConfig(params, extraConfigs, false);
    return axiosInstance[methods](apiKey, config);
  } else if (methods === "post" || methods === "put" || methods === "patch") {
    let params = {};
    params = dataObj ? dataObj : params;
    const config = getConfig(params, extraConfigs, true);
    // //TODO: Need to remove this log before moving in production
    // console.log(
    //   `Making api call with url: ${apiKey}, headers: ${JSON.stringify(config)}`
    // );
    return axiosInstance[methods](apiKey, dataObj, config);
  } else {
    return Promise.reject("Invalid method");
  }
};

const generateCacheKey = (
  url: string,
  cachePrefix: string | null = null
): string => {
  /**
   * - Remove https from the url.
   * - Replace all / with __
   * - replace all ? with _
   * -
   */

  let key = url
    .replaceAll("https://", "")
    .replaceAll(/\//g, "__")
    .replaceAll(/\?/g, "_")
    .replaceAll("&", "-")
    .replaceAll("=", "---");

  // Always add hito_ prefix
  if (cachePrefix) return `hito_${cachePrefix}__${key}`;

  return `hito_${key}`;
};

export const post = async (
  apiKey,
  params = {},
  extraConfigs = {}
): Promise<any> => {
  const response =
    apiKey && (await axiosRequest(apiKey, "post", params, extraConfigs));
  return response;
};

export const patch = async (
  apiKey,
  params = {},
  extraConfigs = {}
): Promise<any> => {
  const response =
    apiKey && (await axiosRequest(apiKey, "patch", params, extraConfigs));
  return response;
};

export const getWithCache = async (
  url: string,
  params: object = {},
  extraConfigs: object = {},
  cachePrefix: string | null = null
) => {
  const cacheKey = generateCacheKey(url, cachePrefix);
  try {
    const data = await fetchCachedData(cacheKey);
    if (data && config.IS_PROD === "true") {
      return data;
    } else {
      // console.log(`Cache miss for ${url}, fetching fresh data`);
      const response =
        url && (await axiosRequest(url, "get", params, extraConfigs));
      if (response && config.IS_PROD === "true") {
        await updateCacheData(cacheKey, response?.data);
      }
      // console.log("Response getWithCache: ", response);
      return response?.data;
    }
  } catch (e) {
    // console.log("Error in getWithCache: ", e);
    return Promise.reject(e);
  }
};

export const get = async (
  apiKey,
  params = {},
  extraConfigs = {}
): Promise<any> => {
  return apiKey && (await axiosRequest(apiKey, "get", params, extraConfigs));
};

export const del = async (
  apiKey,
  params = {},
  extraConfigs = {}
): Promise<any> => {
  const response =
    apiKey && (await axiosRequest(apiKey, "delete", params, extraConfigs));
  return response;
};

export const put = async (
  apiKey,
  params = {},
  extraConfigs = {}
): Promise<any> => {
  const response =
    apiKey && (await axiosRequest(apiKey, "put", params, extraConfigs));
  return response;
};

export const getBrowserCountry = () => {
  return getCookie("topmate_country") || "IN";
};
